<template>
  <div class="p-6 space-y-6 max-w-5xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-bold" style="color:var(--gray-900);">🤖 AI 小幫手使用統計</h1>
        <div class="text-sm mt-1" style="color:var(--gray-500);">追蹤使用者詢問的功能類別與問題趨勢</div>
      </div>
      <div class="flex items-center gap-3">
        <select v-model="days" @change="load" class="input" style="width:120px;">
          <option :value="7">近 7 天</option>
          <option :value="30">近 30 天</option>
          <option :value="90">近 90 天</option>
        </select>
        <button @click="load" :disabled="loading" class="btn" style="background:#4f46e5;color:#fff;border:none;">
          {{ loading ? '載入中...' : '🔄 重新整理' }}
        </button>
      </div>
    </div>

    <div v-if="error" class="card p-4 text-sm" style="color:#b91c1c;background:#fee2e2;">{{ error }}</div>

    <!-- Summary Cards -->
    <div v-if="stats" class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="card p-4 text-center">
        <div class="text-xs" style="color:var(--gray-500);">總問題數</div>
        <div class="text-3xl font-bold mt-1" style="color:#4f46e5;">{{ stats.total }}</div>
      </div>
      <div class="card p-4 text-center">
        <div class="text-xs" style="color:var(--gray-500);">最熱門類別</div>
        <div class="text-base font-bold mt-1" style="color:#7c3aed;">{{ topTopic }}</div>
      </div>
      <div class="card p-4 text-center">
        <div class="text-xs" style="color:var(--gray-500);">類別數</div>
        <div class="text-3xl font-bold mt-1" style="color:#0891b2;">{{ Object.keys(stats.topic_counts).length }}</div>
      </div>
      <div class="card p-4 text-center">
        <div class="text-xs" style="color:var(--gray-500);">日均問題</div>
        <div class="text-3xl font-bold mt-1" style="color:#16a34a;">{{ dailyAvg }}</div>
      </div>
    </div>

    <div v-if="stats" class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Topic breakdown -->
      <div class="card p-5">
        <div class="font-bold mb-4" style="color:var(--gray-800);">📊 功能類別分佈</div>
        <div class="space-y-3">
          <div v-for="(count, topic) in stats.topic_counts" :key="topic">
            <div class="flex items-center justify-between mb-1">
              <span class="text-sm font-medium">{{ topic }}</span>
              <span class="text-sm font-bold" style="color:#4f46e5;">{{ count }}</span>
            </div>
            <div class="h-2 rounded-full" style="background:#f3f4f6;">
              <div class="h-2 rounded-full" :style="{
                width: (count / stats.total * 100).toFixed(1) + '%',
                background: topicColor(topic)
              }"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Daily trend -->
      <div class="card p-5">
        <div class="font-bold mb-4" style="color:var(--gray-800);">📈 每日問題量趨勢</div>
        <div v-if="!Object.keys(stats.daily_trend).length" class="text-sm text-center py-6" style="color:var(--gray-400);">
          此期間無資料
        </div>
        <div v-else class="space-y-1.5" style="max-height:260px;overflow-y:auto;">
          <div v-for="(count, day) in sortedDailyTrend" :key="day" class="flex items-center gap-3">
            <span class="text-xs w-24 flex-shrink-0" style="color:var(--gray-500);">{{ day }}</span>
            <div class="flex-1 h-5 rounded" style="background:#f3f4f6;">
              <div class="h-5 rounded flex items-center px-2" :style="{
                width: Math.max(24, count / maxDailyCount * 100) + '%',
                background: 'linear-gradient(90deg,#4f46e5,#7c3aed)',
                minWidth: count > 0 ? '24px' : '0',
              }">
                <span class="text-xs text-white font-semibold">{{ count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent questions -->
    <div v-if="stats && stats.recent_questions.length" class="card">
      <div class="p-4 border-b" style="border-color:var(--border);">
        <div class="font-bold" style="color:var(--gray-800);">🕐 最近問題記錄</div>
      </div>
      <div class="overflow-x-auto">
        <table class="data-table">
          <thead>
            <tr>
              <th class="px-4 py-2.5" style="width:140px;">時間</th>
              <th class="px-4 py-2.5" style="width:130px;">類別</th>
              <th class="px-4 py-2.5">問題摘要</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="q in stats.recent_questions" :key="q.id" style="border-bottom:1px solid var(--border);">
              <td class="px-4 py-2.5 text-xs" style="color:var(--gray-500);">{{ fmtDate(q.created_at) }}</td>
              <td class="px-4 py-2.5">
                <span class="badge" :style="`background:${topicColor(q.topic)}22;color:${topicColor(q.topic)};border:1px solid ${topicColor(q.topic)}44;`">
                  {{ q.topic || '其他' }}
                </span>
              </td>
              <td class="px-4 py-2.5 text-sm" style="color:var(--gray-700);">{{ q.question }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!stats && !loading" class="card p-8 text-center" style="color:var(--gray-400);">
      點擊「重新整理」載入統計資料
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAppStore, ADMIN_API } from '../store/app.js'
import axios from 'axios'

const store = useAppStore()
const days = ref(30)
const loading = ref(false)
const stats = ref(null)
const error = ref('')

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const res = await axios.get(`${ADMIN_API}/ai/support/stats`, {
      params: { days: days.value },
      headers: { Authorization: `Bearer ${store.token}` },
    })
    stats.value = res.data
  } catch (e) {
    error.value = e.response?.data?.detail || e.message
  } finally {
    loading.value = false
  }
}

const topTopic = computed(() => {
  if (!stats.value?.topic_counts) return '—'
  const entries = Object.entries(stats.value.topic_counts)
  if (!entries.length) return '—'
  return entries.sort((a, b) => b[1] - a[1])[0][0]
})

const dailyAvg = computed(() => {
  if (!stats.value) return 0
  const days_count = Object.keys(stats.value.daily_trend).length || 1
  return (stats.value.total / days_count).toFixed(1)
})

const sortedDailyTrend = computed(() => {
  if (!stats.value?.daily_trend) return {}
  return Object.fromEntries(
    Object.entries(stats.value.daily_trend).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 30)
  )
})

const maxDailyCount = computed(() => {
  const vals = Object.values(stats.value?.daily_trend || {})
  return Math.max(...vals, 1)
})

const TOPIC_COLORS = {
  '郵件收發': '#0891b2',
  'AI郵件輔助': '#7c3aed',
  'AI行銷功能': '#ea580c',
  '聯絡人管理': '#16a34a',
  '旅程自動化': '#d97706',
  '行銷報表': '#4f46e5',
  '行事曆': '#0d9488',
  '安全密碼箱': '#dc2626',
  '帳號設定': '#6b7280',
  '其他': '#9ca3af',
}
const topicColor = (t) => TOPIC_COLORS[t] || '#6b7280'

const fmtDate = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

onMounted(load)
</script>
